import { useEffect, useMemo, useState } from "react";
import { ActionIcon, Center, Flex, Loader, Text, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { IconEye } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { ViewCategoryModal } from "./ViewCategoryModal";

type FundCategoryItem = {
  name: string;
  schemesCount: number | null;
};

const SUPPORTED_SPECIFIC_TARGETS = [
  "Global_Investing",
  "Consumption_Fund",
  "SIF",
  "Defence",
  "Sharia_Compliant_Investments",
  "Gold_Silver",
] as const;

const SPECIFIC_TARGET_ALIASES: Record<string, (typeof SUPPORTED_SPECIFIC_TARGETS)[number]> = {
  halal_ethical_fund: "Sharia_Compliant_Investments",
  sharia_compliant_investments: "Sharia_Compliant_Investments",
  ...Object.fromEntries(SUPPORTED_SPECIFIC_TARGETS.map((target) => [target.toLowerCase(), target])),
};

export default function AssetBasedFundsPage() {
  const theme = useMantineTheme();

  const [categories, setCategories] = useState<FundCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewTarget, setViewTarget] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const res = await trpc.schemes.listSpecificTargets.query();

        const visibleCategories: FundCategoryItem[] = [];
        const seen = new Set<string>();

        for (const raw of res?.targets ?? []) {
          const target = String(raw.specificTarget ?? raw).trim();
          const mappedTarget = SPECIFIC_TARGET_ALIASES[target.toLowerCase()];
          if (mappedTarget && !seen.has(mappedTarget)) {
            seen.add(mappedTarget);
            visibleCategories.push({
              name: mappedTarget,
              schemesCount: raw.selectedCount ?? null,
            });
          }
        }

        setCategories(visibleCategories);
      } catch (err) {
        handleTrpcError(err);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleView = (category: FundCategoryItem) => {
    setViewTarget(category.name);
  };

  const columns: MRT_ColumnDef<FundCategoryItem>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Fund category name",
        size: 320,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Text fz="sm" truncate>
            {cell.getValue<string>()}
          </Text>
        ),
      },
      {
        accessorKey: "schemesCount",
        header: "No. of schemes added",
        size: 200,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => {
          const value = cell.getValue<number | null>();
          return <Text fz="sm">{value ?? "—"}</Text>;
        },
      },
    ],
    []
  );

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      <Flex justify="space-between" align="center" mb="xl">
        <Title order={2} fw={700}>
          Asset Based Funds
        </Title>
      </Flex>

      <MantineReactTable
        columns={columns}
        data={categories}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        positionActionsColumn="last"
        paginationDisplayMode="pages"
        mantinePaginationProps={{
          rowsPerPageOptions: ["10", "25", "50", "100"],
        }}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
          density: "xs",
          pagination: { pageIndex: 0, pageSize: 15 },
          sorting: [{ id: "name", desc: false }],
        }}
        renderRowActions={({ row }) => (
          <Tooltip label="View Details">
            <ActionIcon c="slate.6" variant="transparent" onClick={() => handleView(row.original)}>
              <IconEye size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 220px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
              No categories found.
            </td>
          </tr>
        )}
      />

      <ViewCategoryModal
        opened={viewTarget !== null}
        specificTarget={viewTarget ?? ""}
        onClose={() => setViewTarget(null)}
      />
    </Flex>
  );
}
